import { getElementOffset, getElementPositionFromNode, getNodePositionFromElement, screenToWorld, worldToScreen, } from '@/utils/board/stageUtils';
/**
 * Утилитарные функции для работы с позицией вставки элементов на доске
 */
// Хранилище начальных позиций элементов для синхронизации снаппинга
// при перемещении нескольких элементов одновременно
const dragInitialPositions = new Map();
let dragMainElementId = null;
/**
 * Сохраняет начальные позиции элементов в экранных координатах
 * для синхронизации снаппинга при перемещении нескольких элементов
 *
 * При перемещении нескольких элементов одновременно снаппинг применяется
 * только к главному элементу (который перетаскивается курсором), а остальные
 * элементы перемещаются синхронно с тем же смещением. Для этого необходимо
 * сохранить начальные позиции всех элементов перед началом перетаскивания.
 *
 * @param stage - Stage Konva, на котором находятся элементы
 * @param elementIds - Массив ID элементов, которые будут перемещаться
 * @param mainElementId - ID главного элемента (который перетаскивается курсором)
 */
export function saveDragInitialPositions(stage, elementIds, mainElementId) {
    dragInitialPositions.clear();
    dragMainElementId = mainElementId;
    for (const id of elementIds) {
        const node = stage.findOne(`#node-${id}`);
        if (node) {
            const absPos = node.absolutePosition();
            dragInitialPositions.set(id, { x: absPos.x, y: absPos.y });
        }
    }
}
/**
 * Очищает сохраненные начальные позиции элементов
 *
 * Вызывается после окончания перетаскивания для очистки состояния
 * и предотвращения утечек памяти.
 */
export function clearDragInitialPositions() {
    dragInitialPositions.clear();
    dragMainElementId = null;
}
/**
 * Получает позицию для вставки элемента на доске
 *
 * Определяет позицию вставки элемента в зависимости от положения курсора:
 * - Если курсор внутри канваса, использует позицию курсора
 * - Если курсор вне канваса и нет Stage, вставляет в центр окна
 * - Если курсор вне канваса, вставляет в центр доски
 *
 * @param pointerPositionRef - Референс на позицию указателя мыши (в world координатах)
 * @param getStage - Функция для получения Stage компонента Konva
 * @param zoom - Текущий уровень зума доски
 * @returns Координаты для вставки элемента в world координатах
 */
export function getInsertPosition(pointerPositionRef, getStage, zoom) {
    if (pointerPositionRef.current) {
        // Если курсор внутри канваса — используем его
        return pointerPositionRef.current;
    }
    const stage = getStage();
    if (!stage) {
        // Если курсор вне канваса и нет Stage — вставляем в центр окна
        return {
            x: Math.round(window.innerWidth / 2 / zoom),
            y: Math.round(window.innerHeight / 2 / zoom),
        };
    }
    // Если курсор вне канваса — вставляем в центр доски
    const scale = stage.scaleX();
    const x = (window.innerWidth / 2 - stage.x()) / scale;
    const y = (window.innerHeight / 2 - stage.y()) / scale;
    return { x, y };
}
/**
 * Получает offset элемента из состояния доски
 *
 * Offset используется для центрирования вращения элемента вокруг его центра.
 * Вычисляется как половина ширины и высоты элемента.
 *
 * @param boardState - Состояние доски, содержащее информацию об элементах
 * @param elementId - ID элемента, для которого нужно получить offset
 * @returns Объект с offsetX и offsetY (половина ширины и высоты элемента)
 */
function getElementOffsetFromState(boardState, elementId) {
    var _a, _b;
    for (const layer of boardState.layers) {
        const element = layer.elements.find((el) => el.id === elementId);
        if (element) {
            const elW = ((_a = element.props) === null || _a === void 0 ? void 0 : _a.w) || 64;
            const elH = ((_b = element.props) === null || _b === void 0 ? void 0 : _b.h) || 64;
            return getElementOffset(elW, elH);
        }
    }
    return { offsetX: 0, offsetY: 0 };
}
/**
 * Применяет снаппинг к позиции главного элемента
 *
 * Выполняет следующие преобразования:
 * 1. Преобразует экранные координаты узла (с offset) в world координаты
 * 2. Вычитает offset, чтобы получить реальную позицию элемента (левый верхний угол)
 * 3. Применяет снаппинг к реальной позиции элемента
 * 4. Добавляет offset обратно и преобразует в экранные координаты
 *
 * @param stage - Stage Konva
 * @param pos - Позиция узла в экранных координатах (с offset)
 * @param elementId - ID элемента (для определения локальной сетки)
 * @param elementOffset - Offset элемента для центрирования вращения
 * @param gridSize - Размер сетки для снаппинга
 * @param snapToGrid - Флаг включения снаппинга к сетке
 * @param boardState - Состояние доски (для определения локальных сеток)
 * @param getSnappedXY - Функция для получения привязанных координат
 * @returns Позиция узла в экранных координатах после снаппинга
 */
function applySnappingToMainElement(stage, pos, elementId, elementOffset, gridSize, snapToGrid, boardState, getSnappedXY) {
    // Преобразуем экранные координаты узла в world координаты
    const worldNode = screenToWorld(stage, pos.x, pos.y);
    // Вычитаем offset, чтобы получить реальную позицию элемента (левый верхний угол)
    const worldElement = getElementPositionFromNode(worldNode.x, worldNode.y, elementOffset.offsetX, elementOffset.offsetY);
    // Преобразуем обратно в экранные координаты для снаппинга
    const screenElement = worldToScreen(stage, worldElement.x, worldElement.y);
    // Применяем снаппинг
    const snappedScreen = getSnappedXY(stage, screenElement.x, screenElement.y, gridSize, snapToGrid, boardState.layers, elementId);
    // Преобразуем результат снаппинга в world координаты
    const snappedWorld = screenToWorld(stage, snappedScreen.x, snappedScreen.y);
    // Добавляем offset обратно и преобразуем в экранные координаты
    const snappedWorldNode = getNodePositionFromElement(snappedWorld.x, snappedWorld.y, elementOffset.offsetX, elementOffset.offsetY);
    return worldToScreen(stage, snappedWorldNode.x, snappedWorldNode.y);
}
/**
 * Вычисляет позицию неглавного элемента на основе смещения главного элемента
 *
 * Для синхронизации перемещения нескольких элементов вычисляет смещение
 * главного элемента от его начальной позиции и применяет то же смещение
 * к неглавному элементу. Это обеспечивает синхронное перемещение всех
 * элементов без независимого снаппинга каждого элемента.
 *
 * @param stage - Stage Konva
 * @param elementId - ID неглавного элемента, позицию которого нужно вычислить
 * @returns Позиция элемента в экранных координатах или null, если не удалось вычислить
 */
function calculateSecondaryElementPosition(stage, elementId) {
    const mainInitialPos = dragInitialPositions.get(dragMainElementId);
    const currentInitialPos = dragInitialPositions.get(elementId);
    if (!mainInitialPos || !currentInitialPos) {
        return null;
    }
    const mainNode = stage.findOne(`#node-${dragMainElementId}`);
    if (!mainNode) {
        return null;
    }
    // Получаем текущую позицию главного элемента после снаппинга
    const mainCurrentPos = mainNode.absolutePosition();
    // Вычисляем смещение главного элемента
    const deltaX = mainCurrentPos.x - mainInitialPos.x;
    const deltaY = mainCurrentPos.y - mainInitialPos.y;
    // Применяем то же смещение к текущему элементу
    return {
        x: currentInitialPos.x + deltaX,
        y: currentInitialPos.y + deltaY,
    };
}
/**
 * Создает функцию для ограничения перемещения элемента с поддержкой снаппинга
 *
 * Эта функция используется как `dragBoundFunc` в Konva для ограничения
 * перемещения элементов при перетаскивании. Она обеспечивает:
 *
 * 1. Снаппинг к сетке для главного элемента (который перетаскивается курсором)
 * 2. Синхронное перемещение остальных элементов с тем же смещением
 * 3. Поддержку локальных сеток (если элемент находится внутри элемента с локальной сеткой)
 * 4. Правильную обработку offset элементов для центрирования вращения
 *
 * **Важно:** При перемещении нескольких элементов снаппинг применяется только
 * к главному элементу, чтобы избежать неравномерного перемещения элементов.
 *
 * @param getStage - Функция для получения Stage компонента Konva
 * @param gridSize - Размер сетки для снаппинга (в world координатах)
 * @param getSnapToGrid - Функция для получения актуального значения флага привязки к сетке
 * @param getBoardState - Функция или объект для получения актуального состояния доски
 * @param getSnappedXY - Функция для получения привязанных координат с учетом локальных сеток
 * @returns Функция ограничения перемещения, которая принимает позицию в экранных
 *          координатах и возвращает скорректированную позицию в экранных координатах
 */
export function createElementDragBoundFunc(getStage, gridSize, getSnapToGrid, getBoardState, getSnappedXY) {
    return (pos, elementId) => {
        const stage = getStage();
        if (!stage)
            return pos;
        const boardState = typeof getBoardState === 'function' ? getBoardState() : getBoardState;
        if (!(boardState === null || boardState === void 0 ? void 0 : boardState.layers))
            return pos;
        // Для неглавных элементов используем относительное смещение от главного элемента
        const isSecondaryElement = elementId !== undefined &&
            dragMainElementId !== null &&
            elementId !== dragMainElementId &&
            dragInitialPositions.has(dragMainElementId) &&
            dragInitialPositions.has(elementId);
        if (isSecondaryElement) {
            const secondaryPos = calculateSecondaryElementPosition(stage, elementId);
            if (secondaryPos) {
                return secondaryPos;
            }
        }
        // Для главного элемента применяем снаппинг
        const elementOffset = getElementOffsetFromState(boardState, elementId);
        return applySnappingToMainElement(stage, pos, elementId, elementOffset, gridSize, getSnapToGrid(), boardState, getSnappedXY);
    };
}
